import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Contact from "../components/Contact";
import { SEO, localBusinessSchema, breadcrumbSchema } from "../components/SEO";
import { BRAND, GALLERY, LOGO_URL } from "../mock";
import { Instagram, Play, ExternalLink } from "lucide-react";

// Curated Instagram-style media tiles (mock posts. swap with real Instagram embeds when ready)
const REELS = [
  { id: 1, type: "video", caption: "Crystal Serum Light + EXO v5 going down on a Porsche 992 \ud83d\udd25", img: GALLERY[0] },
  { id: 2, type: "image", caption: "Two-layer Gtechniq applied. 5 years locked in.", img: GALLERY[2] },
  { id: 3, type: "video", caption: "Water beading test. EXO v5 doing what it does best.", img: GALLERY[1] },
  { id: 4, type: "image", caption: "Three-stage paint correction on a black M4. Defects gone.", img: GALLERY[4] },
  { id: 5, type: "video", caption: "Wheel ceramic. brake dust just rinses off.", img: GALLERY[5] },
  { id: 6, type: "image", caption: "BMW M440i delivery. Crystal Serum Light + EXO v5.", img: GALLERY[3] },
  { id: 7, type: "video", caption: "Gtechniq G1 ClearVision on the windshield. Wipers off, please.", img: GALLERY[7] },
  { id: 8, type: "image", caption: "Interior ceramic complete. Stain-proofed leather and fabric.", img: GALLERY[6] },
];

export default function MediaPage() {
  const url = `${BRAND.url}/media`;
  const [filter, setFilter] = useState("all");
  const filtered =
    filter === "all" ? REELS : REELS.filter((r) => r.type === filter);

  return (
    <div className="grain bg-jester-deep text-white min-h-screen">
      <SEO
        title="Videos, Reels & Instagram | Jester Werks Ceramic Coating"
        description="Watch Jester Werks' latest ceramic coating reels, paint correction time-lapses and Gtechniq Crystal Serum Light + EXO v5 application videos. Mississauga's most-reviewed ceramic studio."
        canonical={url}
        schemas={[
          localBusinessSchema(),
          breadcrumbSchema([
            { name: "Home", url: `${BRAND.url}/` },
            { name: "Videos & Media", url },
          ]),
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute -top-20 right-1/4 w-[600px] h-[600px] rounded-full bg-jester-purple/15 blur-[140px]" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-acid/8 blur-[140px]" />
        <img
          src={LOGO_URL}
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute right-[8%] top-1/2 -translate-y-1/2 w-[300px] opacity-60 spin-slow"
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <a
              href={BRAND.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#A8E000]/40 bg-[#A8E000]/8 backdrop-blur-sm mb-8 hover:bg-acid/15 transition-colors"
            >
              <Instagram className="w-3.5 h-3.5 text-acid" />
              <span className="text-xs font-label uppercase tracking-[0.3em] text-acid">
                Live on @jesterwerks.gta
              </span>
            </a>

            <h1 className="font-display text-[12vw] md:text-[6.5rem] leading-[0.92] tracking-tight">
              REELS &amp;<br />
              <span className="text-jester">RESULTS.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed">
              Time-lapses, water-beading tests, paint correction breakdowns,
              and full Gtechniq applications. Watch the work happen in our
              climate-controlled bay.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              {[
                { id: "all", label: "All" },
                { id: "video", label: "Reels" },
                { id: "image", label: "Photos" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-5 h-10 rounded-full font-label tracking-[0.2em] text-xs uppercase transition-colors ${
                    filter === f.id
                      ? "bg-acid text-[#120127]"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-2 px-5 h-10 rounded-full bg-jester-purple hover:bg-jester text-white font-label tracking-[0.2em] text-xs uppercase transition-colors"
              >
                <Instagram className="w-4 h-4" /> Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {filtered.map((r) => (
              <a
                key={r.id}
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="relative aspect-[4/5] rounded-xl overflow-hidden group border border-white/5 hover:border-jester transition-colors hover-lift"
              >
                <img
                  src={r.img}
                  alt={r.caption}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#120127] via-[#120127]/30 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />

                {r.type === "video" && (
                  <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-acid group-hover:text-[#120127] transition-colors">
                    <Play className="w-4 h-4 fill-current" />
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white text-sm leading-snug line-clamp-3">
                    {r.caption}
                  </p>
                  <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-acid font-label opacity-0 group-hover:opacity-100 transition-opacity">
                    <Instagram className="w-3 h-3" /> View on Instagram{" "}
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Follow CTA strip */}
          <a
            href={BRAND.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-5 rounded-2xl border border-jester/30 bg-jester-purple/10 px-8 py-7 hover:border-acid hover:bg-jester-purple/15 transition-colors group"
          >
            <div>
              <div className="flex items-center gap-3">
                <Instagram className="w-6 h-6 text-acid" />
                <span className="font-display text-2xl tracking-wide">
                  @jesterwerks.gta
                </span>
              </div>
              <p className="text-white/65 mt-2">
                Follow for daily Gtechniq applications, water-bead tests, and
                before/after time-lapses.
              </p>
            </div>
            <span className="flex items-center gap-2 text-acid font-label tracking-[0.2em] text-sm uppercase">
              Follow Now <ExternalLink className="w-4 h-4" />
            </span>
          </a>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
